import { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

// Rotas que não precisam de verificação de restaurante
const SKIP_RESTAURANT_CHECK_PATHS = [
  '/internal-support-alc2024',
  '/auth',
  '/pending-approval',
  '/access-denied',
  '/approval-success',
  '/forgot-password',
  '/reset-password',
  '/terms',
  '/privacy',
];

// Zod schema for restaurant data validation - security defense-in-depth
const RestaurantDataSchema = z.object({
  restaurantName: z.string()
    .min(2, 'Nome deve ter no mínimo 2 caracteres')
    .max(200, 'Nome muito longo')
    .regex(/^[a-zA-ZÀ-ÿ0-9\s&\-.']+$/, 'Nome contém caracteres inválidos'),
  phone: z.string()
    .regex(/^[\d\s\-+()]*$/, 'Formato de telefone inválido')
    .max(20, 'Telefone muito longo')
    .optional()
    .nullable()
    .transform(val => val || null),
  whatsapp: z.string()
    .regex(/^[\d\s\-+()]*$/, 'Formato de WhatsApp inválido')
    .max(20, 'WhatsApp muito longo')
    .optional()
    .nullable()
    .transform(val => val || null),
  address: z.string()
    .max(500, 'Endereço muito longo')
    .optional()
    .nullable()
    .transform(val => val || null),
  city: z.string()
    .max(100, 'Cidade muito longa')
    .optional()
    .nullable()
    .transform(val => val || null),
  state: z.string()
    .max(2, 'Estado deve ter 2 letras')
    .optional()
    .nullable()
    .transform(val => val || null),
  zipCode: z.string()
    .regex(/^(\d{5}-?\d{3})?$/, 'CEP inválido')
    .optional()
    .nullable()
    .transform(val => val || null),
  instagram: z.string()
    .regex(/^(@?[a-zA-Z0-9._]*)?$/, 'Instagram inválido')
    .max(100, 'Instagram muito longo')
    .optional()
    .nullable()
    .transform(val => val || null),
  website: z.string()
    .max(200, 'Website muito longo')
    .optional()
    .nullable()
    .transform(val => val || null),
  facebook: z.string()
    .max(200, 'Facebook muito longo')
    .optional()
    .nullable()
    .transform(val => val || null),
});

interface BranchData {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  cnpj?: string;
  whatsapp?: string;
  instagram?: string;
  phone?: string;
  managerName?: string;
  managerEmail?: string;
}

interface ManualReferralInfo {
  type: string;
  name: string;
  manual: boolean;
}

interface RestaurantData {
  restaurantName: string;
  cnpj?: string;
  totalUnits?: string;
  businessNiche?: string;
  phone?: string;
  whatsapp?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  instagram?: string;
  website?: string;
  facebook?: string;
  branches?: BranchData[];
  manualReferralInfo?: ManualReferralInfo;
  selectedPlan?: 'basic' | 'premium';
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, name: string, restaurantData: RestaurantData, referralCode?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    let isMounted = true;
    // Track if user was ever authenticated to prevent false logouts during token refresh
    const hasHadSession = { current: false };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!isMounted) return;

        // GUARD: Don't clear state during token refresh edge cases
        if (event === 'TOKEN_REFRESHED' && !newSession) {
          return;
        }

        // GUARD: If we had a session and now it's null,
        // only act on explicit SIGNED_OUT events
        if (hasHadSession.current && !newSession && event !== 'SIGNED_OUT') {
          return;
        }

        if (newSession) {
          hasHadSession.current = true;
        }

        // STABILITY FIX: Only update session if access_token actually changed
        // This prevents re-renders on tab switch when token hasn't expired
        setSession(prev => {
          if (prev?.access_token === newSession?.access_token) return prev;
          return newSession;
        });

        // OPTIMIZATION: Only update user state if user actually changed
        setUser(prev => {
          const newUser = newSession?.user ?? null;
          if (prev?.id === newUser?.id) return prev;
          return newUser;
        });

        // ONLY check restaurant on FIRST sign-in, NOT on re-connections
        if (event === 'SIGNED_IN' && newSession?.user) {
          // STABILITY FIX: If user already had a session, this is a re-connection, not a fresh login
          if (hasHadSession.current) return;

          const currentPath = window.location.pathname;
          const shouldSkipCheck = SKIP_RESTAURANT_CHECK_PATHS.some(
            path => currentPath.startsWith(path)
          );

          if (!shouldSkipCheck) {
            setTimeout(() => {
              checkUserRestaurant(newSession.user.id);
            }, 0);
          }
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      if (!isMounted) return;
      if (existingSession) {
        hasHadSession.current = true;
      }
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      setLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const checkUserRestaurant = async (userId: string) => {
    try {
      const currentPath = window.location.pathname;
      if (SKIP_RESTAURANT_CHECK_PATHS.some(path => currentPath.startsWith(path))) {
        return;
      }

      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      
      const isSupportUser = roles?.some(r => r.role === 'support') ?? false;
      if (isSupportUser) {
        return;
      }

      const { data: membership } = await supabase
        .from('restaurant_members')
        .select('restaurant_id')
        .eq('user_id', userId)
        .maybeSingle();

      if (membership) {
        return;
      }

      const { data: restaurant } = await (supabase as any)
        .from('restaurants')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (!restaurant) {
        navigate('/dashboard?onboarding=true');
      }
    } catch (error) {
      console.error('Error checking restaurant:', error);
    }
  };

  const signUp = useCallback(async (email: string, password: string, name: string, restaurantData: RestaurantData, referralCode?: string) => {
    try {
      const validatedData = RestaurantDataSchema.safeParse(restaurantData);
      
      if (!validatedData.success) {
        const firstError = validatedData.error.errors[0];
        toast.error(`Dados inválidos: ${firstError.message}`);
        throw new Error(firstError.message);
      }
      
      const safeRestaurantData = validatedData.data;
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            name,
            restaurant_name: restaurantData.restaurantName,
            referred_by: referralCode || null,
          }
        }
      });

      if (error) throw error;

      if (data.user) {
        const { data: fnResult, error: fnError } = await supabase.functions.invoke('create-restaurant-signup', {
          body: {
            user_id: data.user.id,
            name: safeRestaurantData.restaurantName,
            niche: restaurantData.businessNiche || 'Restaurante',
            cnpj: restaurantData.cnpj || null,
            declared_total_units: parseInt(restaurantData.totalUnits || '1', 10),
            phone: safeRestaurantData.phone,
            whatsapp_number: safeRestaurantData.whatsapp,
            address: safeRestaurantData.address,
            city: safeRestaurantData.city,
            state: safeRestaurantData.state,
            zip_code: safeRestaurantData.zipCode,
            instagram: safeRestaurantData.instagram,
            website: safeRestaurantData.website,
            facebook: safeRestaurantData.facebook,
            contact_email: email,
            contact_name: name.substring(0, 200),
            contact_phone: safeRestaurantData.whatsapp || safeRestaurantData.phone,
            // Unified: all referral codes go in referred_by_code
            referred_by_code: referralCode ? referralCode.substring(0, 50) : null,
            referred_by_partner_slug: null, // Deprecated: kept for DB compat
            other_social_media: restaurantData.manualReferralInfo 
              ? { referral_source: restaurantData.manualReferralInfo }
              : null,
            branches: restaurantData.branches || [],
            selected_plan: restaurantData.selectedPlan || 'premium',
          },
        });

        if (fnError || !fnResult?.success) {
          console.error('Error creating restaurant via edge function:', fnError, fnResult);
          throw new Error(fnResult?.error || fnError?.message || 'Erro ao criar restaurante');
        }

        toast.success('Cadastro realizado! Aguarde a aprovação da nossa equipe.');
        navigate('/pending-approval');
      }
    } catch (error: any) {
      if (error.message?.includes('User already registered')) {
        toast.error('Este email já está cadastrado. Faça login.');
      } else {
        toast.error(error.message || 'Erro ao criar conta');
      }
      throw error;
    }
  }, [navigate]);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      if (data.user) {
        try {
          await (supabase as any).rpc('increment_login_count', { p_user_id: data.user.id });
        } catch (rpcError) {
          console.warn('Failed to increment login count:', rpcError);
        }
      }

      await queryClient.invalidateQueries({ queryKey: ['subscription'] });
      await queryClient.invalidateQueries({ queryKey: ['retention-offer'] });

      toast.success('Login realizado com sucesso!');
    } catch (error: any) {
      if (error.message.includes('Invalid login credentials')) {
        toast.error('Email ou senha incorretos');
      } else {
        toast.error(error.message || 'Erro ao fazer login');
      }
      throw error;
    }
  }, [queryClient]);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (error: any) {
      console.error('Error during sign out:', error);
    } finally {
      queryClient.clear();
      
      const versionKey = 'alacart-last-app-version';
      const version = localStorage.getItem(versionKey);
      localStorage.clear();
      if (version) localStorage.setItem(versionKey, version);
      sessionStorage.clear();
      
      setUser(null);
      setSession(null);
      toast.success('Logout realizado com sucesso!');
      navigate('/auth');
    }
  }, [navigate, queryClient]);

  const value = useMemo(() => ({
    user, session, loading, signUp, signIn, signOut
  }), [user, session, loading, signUp, signIn, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
